import KroBackButton from "features/components/buttons/kroBackButton";
import { useForm } from "@tanstack/react-form";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSocialTemplate } from "../hooks/useSocialTemplate";
import { TemplateForm } from "@/features/dashboard/domain/services/socialTemplateService";
import SampleMessage from "../components/template/sampleMessage";

type SocialPlatform = "whatsapp" | "instagram" | "x";

interface PlatformConfig {
    title: string;
    description: string;
    image: string;
    shareType: "whatsapp" | "instagram-story" | "x-tweet";
    buttonTitle: string;
    sampleMessages: Array<{
        id: string;
        question: string;
    }>;
}

const PLATFORM_CONFIGS: Record<SocialPlatform, PlatformConfig> = {
    whatsapp: {
        title: "WhatsApp Templates",
        description: "Customize and share your fundraising messages with friends and family",
        image: "/assets/images/whatsapp-lg.svg",
        shareType: "whatsapp",
        buttonTitle: "Share to WhatsApp",
        sampleMessages: [
            {
                id: "item-1",
                question: "Donation request to family and friends",
            },
            {
                id: "item-2",
                question: "Donation request to Co-workers",
            },
        ],
    },
    instagram: {
        title: "Instagram Templates",
        description: "Customize and share your fundraising messages with friends and family",
        image: "/assets/images/instagram-lg.svg",
        shareType: "instagram-story",
        buttonTitle: "Share to Instagram",
        sampleMessages: [
            {
                id: "item-1",
                question: "Donation request to specific instagram user",
            },
            {
                id: "item-2",
                question: "Donation request on story",
            },
        ],
    },
    x: {
        title: "X Templates",
        description: "Customize and share your fundraising messages with friends and family",
        image: "/assets/images/x-lg.svg",
        shareType: "x-tweet",
        buttonTitle: "Share to X",
        sampleMessages: [
            {
                id: "item-1",
                question: "Donation request tweet",
            },
            // {
            //     id: "item-2",
            //     question: "Donation request thread",
            // },
        ],
    },
};

const SocialTemplatePage = () => {
    const navigate = useNavigate();
    const { platform } = useParams<{ platform: SocialPlatform }>();
    const [openItem, setOpenItem] = useState<string | null>("item-1");

    const {
        copied,
        isLoading,
        copyToClipboard,
        share,
        getFundraisingOptions
    } = useSocialTemplate();

    const form = useForm<TemplateForm>({
        defaultValues: {
            recipientName: "[insert name],",
            daysRemaining: "[number],",
            fundraisingPurpose: "a new computer",
            numberOfPeople: "[insert number]",
            donationAmount: "[insert amount]",
            fundraisingGoal: "[be able to meet my fundraising goal]",
            accomplishment: "[accomplishment]",
            fundraiserLink: "[https://giv.com/helping_tega_get_a_new_computer]",
            userName: "[insert name]",
        },
    });

    // Redirect if platform is not supported
    if (!platform || !PLATFORM_CONFIGS[platform]) {
        navigate("/dashboard/templates");
        return null;
    }

    const config = PLATFORM_CONFIGS[platform];

    const fundraisingOptions = getFundraisingOptions();

    const handleCopyToClipboard = () => {
        copyToClipboard(form.state.values);
    };

    const handleShare = () => {
        share(config.shareType, form.state.values);
    };

    const SAMPLE_MESSAGES = config.sampleMessages.map((item) => ({
        ...item,
        sampleMessage: (
            <SampleMessage
                fundraisingOptions={fundraisingOptions}
                form={form}
                handleShareToWhatsApp={handleShare}
                handleCopyToClipboard={handleCopyToClipboard}
                isLoading={isLoading}
                copied={copied}
                title={config.buttonTitle}
            />
        ),
    }));

    const toggleItem = (itemId: string) => {
        setOpenItem(openItem === itemId ? null : itemId);
    };

    return (
        <div className="mx-2 mt-[50px] max-w-[872px] lg:mx-auto lg:mt-[64px]">
            <KroBackButton
                classname="lg:flex hidden"
                onclick={() => {
                    navigate(-1);
                }}
            />
            <div className="mt-[37px] lg:mt-[40px]">
                <div className="flex flex-col gap-2">
                    <p className="font-inter text-[20px] font-semibold leading-[100%] tracking-[-1.5%] text-strong-950 lg:text-[32px] lg:leading-[40px] lg:tracking-[-1.5%]">
                        {config.title}
                    </p>
                    <p className="font-inter text-[16px] font-normal leading-[24px] tracking-[-1.5%] text-neutral-700 lg:text-[20px] lg:leading-[28px] lg:tracking-[0em]">
                        {config.description}
                    </p>
                </div>
                <div className="mt-[32px] lg:mt-[40px]">
                    <img
                        src={config.image}
                        alt={platform}
                        className="w-full lg:h-[332px]"
                    />
                    <div className="mt-[32px] flex flex-col gap-4">
                        <p className="font-inter text-[20px] font-semibold leading-[100%] tracking-[-1.5%] text-strong-950 lg:text-[32px] lg:leading-[40px]">
                            Before You Launch: The 5 People You Should Talk To
                        </p>
                        <p className="font-inter text-[16px] font-normal leading-[24px] tracking-[-1.5%] text-neutral-700 lg:text-[20px] lg:leading-[40px] lg:text-[#000000]">
                            Before you even hit "Create Campaign" on Giv, there's one simple
                            action that can dramatically increase your chances of success:
                            have a conversation with five people. These five people aren't
                            just any five—they're your core supporters. The ones who will
                            stand by you, give early, and spread the word. Let's break down
                            who they are, why they matter, and how to approach them.
                        </p>
                    </div>
                    <div className="mt-[24px] gap-4 lg:mt-[32px]">
                        <p className="font-inter text-[20px] font-semibold leading-[100%] tracking-[-1.5%] text-strong-950 lg:text-[32px] lg:leading-[40px]">
                            Sample messages
                        </p>
                    </div>
                    <div className="mt-[12px] lg:mt-[20px]">
                        <div className="flex w-full flex-row items-center gap-3 rounded-[12px] border border-[#FFEEE5] bg-[#FFF5F0] p-2">
                            <img
                                src="/assets/icons/error-warning-fill-red.svg"
                                alt="info"
                                className="h-4 w-4"
                            />
                            <p className="font-inter text-[12px] font-normal leading-[20px] tracking-[-1.5%] text-[#421600] lg:text-[14px]">
                                Templates can be edited and shared right here in Giv
                            </p>
                        </div>
                        <div className="mt-8 h-full max-w-full lg:mt-[20px]">
                            {SAMPLE_MESSAGES.map((item) => (
                                <div
                                    onClick={() => toggleItem(item.id)}
                                    className={`mb-[24px] mt-[20px] flex cursor-pointer flex-row border-b py-4 transition-colors duration-200 ${openItem === item.id
                                        ? "border-stroke-soft-200"
                                        : "border-[#D2D2D2]"
                                        } `}
                                    key={item.id}
                                >
                                    <div className="flex flex-1 flex-col">
                                        <div className="flex flex-row justify-between">
                                            <span className="text-lg lg:text-2xl flex-5 text-start font-normal">
                                                {item.question}
                                            </span>
                                            <img
                                                src={`/assets/icons/${openItem === item.id ? "up-arrow" : "arrow-right"}.svg`}
                                                alt="arrow"
                                                className={`h-[24px] w-[24px]`}
                                            />
                                        </div>
                                        {openItem === item.id && (
                                            <div className="mt-4">
                                                {item.sampleMessage}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SocialTemplatePage; 